import React, { ComponentType, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { IBasicStyledProps, Toaster } from '@notino/react-styleguide-v20';

import { useApplicationRuntimeContext } from '@context/ApplicationRuntimeContext';
import { useForceUpdate } from '@hooks';
import { IBasicProductQuantity } from '@model';
import { ReactFCV } from '@types';
import { nonAwaitedPromiseHandlerParametrized } from '@utils/async';

import { ButtonQuantitySelector } from '../../../Inputs';
import { useGeneralProductItemContext } from '../context';
import { ISizeProps } from '../model';
import { StyledQuantityCol, StyledDropDownQuantitySelector, UndoButtonStyled } from '../styled';

import { messages } from './messages';

export interface IQuantityProps extends ISizeProps, Omit<IBasicStyledProps, 'className'> {
  quantityCounterDisabled: boolean;
  unit: string;
  productsInCartCount?: number;
  showUnit?: boolean;
  onQuantityChange?: (quantity: number) => Promise<boolean>;
  deleteButtonClick?: () => void;
  addProductToCart?: () => Promise<number>;
  QuantitySelectorComponent?: ComponentType<IBasicProductQuantity>;
}

export const Quantity: ReactFCV<IQuantityProps> = ({
  quantityCounterDisabled,
  unit,
  productsInCartCount: productsCount,
  showUnit,
  onQuantityChange,
  deleteButtonClick,
  addProductToCart,
  QuantitySelectorComponent,
  ...rest
}) => {
  const { maximumQuantity, itemQuantity, disabled, isSummaryLayout } = useGeneralProductItemContext();
  const [value, forceUpdate] = useForceUpdate();
  const { formatMessage } = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [queuedQuantity, setQueuedQuantity] = useState<number>(itemQuantity);
  const debounceDelay = 300;

  const {
    applicationSettings: {
      features: { enablePlusMinusButtonsOnCart },
      secondaryCurrency,
    },
  } = useApplicationRuntimeContext();

  const isQuantityChangeDisabled = disabled || quantityCounterDisabled;
  const isDualPriceLayout = Boolean(secondaryCurrency);

  /*
    Quantity selector from Notino React Styleguide has internal state for handling selected option.
    When product quantity update fails and the product quantity remains the same the internal state
    and external state did differ. Therefore we have introduced useForceUpdate method, which forces
    the QuantitySelector to rerender and adopt the parental state.
   */
  const handleOnQuantityChange = useCallback(
    (quantity: number) => {
      const quantityChange = async () => {
        setIsLoading(true);
        try {
          const success = await onQuantityChange?.(quantity);
          if (!success) {
            setQueuedQuantity(itemQuantity);
            forceUpdate();
          }
        } finally {
          setIsLoading(false);
        }
      };

      nonAwaitedPromiseHandlerParametrized(quantityChange, quantity);
    },
    [onQuantityChange, itemQuantity, forceUpdate],
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      if (queuedQuantity !== itemQuantity) {
        handleOnQuantityChange(queuedQuantity);
      }
    }, debounceDelay);

    return () => clearTimeout(handler);
  }, [handleOnQuantityChange, itemQuantity, queuedQuantity]);

  const handleAdd = useCallback(async () => {
    await addProductToCart?.();
  }, [addProductToCart]);

  const handleRemove = useCallback(() => {
    deleteButtonClick?.();

    if (productsCount && productsCount > 1) {
      Toaster.info({
        content: formatMessage(messages.productRemoved),
        icon: 'IconRegularCheckmark',
        button: (
          <UndoButtonStyled
            onClick={() => {
              void handleAdd();
            }}>
            {formatMessage(messages.undoRemoval)}
          </UndoButtonStyled>
        ),
      });
    }
  }, [deleteButtonClick, productsCount, formatMessage, handleAdd]);

  const handleMinus = useCallback(() => {
    if (queuedQuantity === 1) {
      handleRemove();
    } else {
      setQueuedQuantity((prev) => Math.max(1, prev - 1));
    }
  }, [queuedQuantity, handleRemove]);

  const handlePlus = useCallback(() => {
    setQueuedQuantity((prev) => Math.min(prev + 1, maximumQuantity));
  }, [maximumQuantity]);

  const handleDropDown = useCallback((quantity: number) => {
    setQueuedQuantity(() => quantity);
  }, []);

  return (
    <StyledQuantityCol
      xs={8}
      sm={isSummaryLayout ? 5 : isDualPriceLayout ? 3 : 4}
      md={isSummaryLayout ? 4 : 3}
      {...rest}>
      <Toaster />
      {enablePlusMinusButtonsOnCart ? (
        <ButtonQuantitySelector
          quantityCounterDisabled={isQuantityChangeDisabled}
          showQuantityHandlers={!quantityCounterDisabled}
          quantity={queuedQuantity}
          maximumQuantity={maximumQuantity}
          onMinusButtonAction={handleMinus}
          onPlusButtonAction={handlePlus}
          unit={unit}
          isLoading={isLoading}
          showUnit={showUnit}
        />
      ) : QuantitySelectorComponent ? (
        <QuantitySelectorComponent currentQuantity={itemQuantity} unit={unit} />
      ) : (
        <StyledDropDownQuantitySelector
          key={value}
          disabled={isQuantityChangeDisabled}
          unit={unit}
          currentQuantity={itemQuantity}
          maximumQuantity={maximumQuantity}
          onOptionSelect={handleDropDown}
        />
      )}
    </StyledQuantityCol>
  );
};
Quantity.displayName = 'GeneralProductItem.Quantity';
